import { useSetToast } from "@app/components/Toast";
import { QuestionModal } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/components/QuestionModal";
import {
    AnswerIcon,
    questionTypeToIconName,
    questionTypeToTooltip,
} from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/components/QuestionsTable/columns/answerCol";
import { type QuestionModalConfirmArg } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/types";
import TagListEditor from "@app/shared/components/TagListEditor";
import { type SupportedLocales } from "@app/shared/utils/locales";
import { useOptimistic } from "@app/shared/utils/optimisticUpdates";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexRow } from "@design-system/Layout/Flex";
import {
    CheckboxCell,
    CheckboxHeader,
    type ColumnDef,
} from "@design-system/Table";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text } from "@design-system/Typography/Text";
import { ResponseStatus, TagType } from "@generated/client/graphql";
import { useSetAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { editQuestionIdAtom } from "../../ctx";
import {
    useCreateSupplierTemplateQuestionGroup,
    useCreateTemplateQuestion,
    useDeleteTemplateQuestion,
    useUpdateSupplierTemplateQuestion,
} from "../../data";
import {
    getFirstAvailableTranslation,
    makeTemplateQuestionCreateInput,
} from "../../services";
import { type QuestionListItem } from "../../types";
import { QuestionGroupSelect } from "../QuestionGroupSelect";
import { DeleteQuestionModal } from "./DeleteQuestionModal";
import { useSupplierQuestionsTableTranslations } from "./i18n";

const checkboxCol: ColumnDef<QuestionListItem> = {
    id: "checkbox",
    meta: {
        headerClassName: "min-w-[50px]",
        cellClassName: "min-w-[50px]",
    },
    header: ({ table }) => <CheckboxHeader table={table} />,
    cell: ({ row }) => <CheckboxCell row={row} />,
};

const questionCol = (
    selectedLocale: SupportedLocales,
): ColumnDef<QuestionListItem> => ({
    id: "question",
    meta: {
        colWidth: "60%",
        headerClassName: "max-w-[700px]",
        cellClassName: "max-w-[700px]",
    },
    header: () => {
        const { t, keys } = useSupplierQuestionsTableTranslations();
        return (
            <Text variant="body-sm-bold" color="secondary">
                {t(keys.question)}
            </Text>
        );
    },
    cell: ({ row }) => {
        const { nameMultiLocale, descriptionMultiLocale, type } = row.original;

        const { t, keys } = useSupplierQuestionsTableTranslations();

        const translatedQuestion = nameMultiLocale?.[selectedLocale];
        const translatedDescription = descriptionMultiLocale?.[selectedLocale];

        const firstAvailableTranslation = getFirstAvailableTranslation(
            row.original?.nameMultiLocale,
        );

        return (
            <FlexRow justifyContent="between" w="full">
                <FlexRow gap="2">
                    <AnswerIcon
                        answerType="answer"
                        name={questionTypeToIconName[type]}
                        tooltip={t(questionTypeToTooltip[type])}
                    />
                    {translatedQuestion ? (
                        <Ellipsis asChild hasTooltip>
                            <Text variant="body" color="primary">
                                {translatedQuestion}
                            </Text>
                        </Ellipsis>
                    ) : (
                        <Tooltip>
                            <Tooltip.Trigger>
                                <Text variant="body" color="secondary">
                                    {t(keys.question_not_translated)}
                                </Text>
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {firstAvailableTranslation}
                            </Tooltip.Content>
                        </Tooltip>
                    )}
                </FlexRow>
                {translatedDescription && (
                    <Tooltip>
                        <Tooltip.Trigger>
                            <Icon name="circleInfo" size="sm" />
                        </Tooltip.Trigger>
                        <Tooltip.Content>
                            {translatedDescription}
                        </Tooltip.Content>
                    </Tooltip>
                )}
            </FlexRow>
        );
    },
});

const questionnaireCol: ColumnDef<QuestionListItem> = {
    id: "questionnaire",
    meta: {
        colWidth: "30%",
    },
    header: () => {
        const { t, keys } = useSupplierQuestionsTableTranslations();
        return (
            <Text variant="body-sm-bold" color="secondary">
                {t(keys.questionnaire)}
            </Text>
        );
    },
    cell: ({ row }) => {
        const tagIds = useMemo(
            () => row.original.tags?.map((tag) => tag.id),
            [row.original.tags],
        );
        const { updateQuestion } = useUpdateSupplierTemplateQuestion();

        const { setToastError } = useSetToast();

        const { t, keys } = useSupplierQuestionsTableTranslations();

        const [optimisticTagIds, setOptimisticTagIds] = useOptimistic(tagIds);

        const handleTagChange = (tagIds: string[]) => {
            setOptimisticTagIds(tagIds);
            updateQuestion(
                {
                    id: row.original.id,
                    input: {
                        tagIds,
                    },
                },
                {
                    onSettled(data, error) {
                        if (
                            error ||
                            data?.updateSupplierTemplateQuestion?.status ===
                                ResponseStatus.Error
                        ) {
                            setToastError(t(keys.toast_error_update_question));
                        }
                    },
                },
            );
        };

        return (
            <TagListEditor
                values={optimisticTagIds}
                onValuesChange={handleTagChange}
                tagType={TagType.Questionnaire}
            />
        );
    },
};

const groupCol = (
    selectedLocale: SupportedLocales,
): ColumnDef<QuestionListItem> => ({
    id: "group",
    meta: {
        colWidth: "10%",
    },
    header: () => {
        const { t, keys } = useSupplierQuestionsTableTranslations();
        return (
            <Text variant="body-sm-bold" color="secondary">
                {t(keys.group)}
            </Text>
        );
    },
    cell: ({ row }) => {
        const { updateQuestion } = useUpdateSupplierTemplateQuestion();

        const { createQuestionGroup } =
            useCreateSupplierTemplateQuestionGroup();

        const { setToastError } = useSetToast();

        const { t, keys } = useSupplierQuestionsTableTranslations();

        const [selectedQuestionGroupId, setSelectedQuestionGroupId] = useState<
            string | undefined
        >(row.original.group?.id);

        const handleSelectedGroupIdChange = (groupId: string | undefined) => {
            setSelectedQuestionGroupId(groupId);
            updateQuestion({
                id: row.original.id,
                input: {
                    questionGroupId: groupId || null,
                },
            });
        };

        const handleQuestionGroupCreate = ({
            englishName,
            frenchName,
        }: {
            englishName?: string;
            frenchName?: string;
        }) => {
            createQuestionGroup(
                {
                    nameMultiLocale: {
                        en: englishName,
                        fr: frenchName,
                    },
                    questionIds: [row.original.id],
                },
                {
                    onSettled(data) {
                        if (
                            data?.createQuestionGroup?.status ===
                                ResponseStatus.Success &&
                            data?.createQuestionGroup?.data?.id
                        ) {
                            setSelectedQuestionGroupId(
                                data?.createQuestionGroup?.data?.id,
                            );
                            return;
                        }
                        setToastError(
                            t(keys.modal_toast_error_create_question_group),
                        );
                    },
                },
            );
        };

        useEffect(() => {
            setSelectedQuestionGroupId(row.original.group?.id);
        }, [row.original.group?.id]);

        return (
            <QuestionGroupSelect
                selectedGroupId={selectedQuestionGroupId}
                onSelectedGroupIdChange={handleSelectedGroupIdChange}
                onQuestionGroupCreate={handleQuestionGroupCreate}
                locale={selectedLocale}
            />
        );
    },
});

const actionCol: ColumnDef<QuestionListItem> = {
    id: "action",
    cell: ({ row }) => {
        const { deleteQuestion } = useDeleteTemplateQuestion();
        const { t, keys } = useSupplierQuestionsTableTranslations();
        const { setToastSuccess, setToastError } = useSetToast();

        const setEditQuestionId = useSetAtom(editQuestionIdAtom);

        const [isDeleteQuestionModalOpen, setIsDeleteQuestionModalOpen] =
            useState<boolean>(false);

        const [isCreateQuestionModalOpen, setIsCreateQuestionModalOpen] =
            useState<boolean>(false);

        const [orderToInsert, setOrderToInsert] = useState<number>(0);

        const { createQuestion } = useCreateTemplateQuestion();

        const handleModalDelete = () => {
            deleteQuestion(row.original.id, {
                onSettled(data, error) {
                    if (
                        error ||
                        data?.deleteSupplierTemplateQuestion?.status ===
                            ResponseStatus.Error
                    ) {
                        setToastError(t(keys.toast_error_delete_question));
                        return;
                    }
                    setToastSuccess(t(keys.toast_success_delete_question));
                },
            });
        };

        const handleCreateQuestion = (question: QuestionModalConfirmArg) => {
            const input = makeTemplateQuestionCreateInput(question);
            createQuestion(input);
            setIsCreateQuestionModalOpen(false);
        };

        const handleDelete = () => {
            setIsDeleteQuestionModalOpen(true);
        };

        const handleEdit = () => {
            return setEditQuestionId(row.original.id);
        };

        const handleInsertAbove = () => {
            const calculatedOrder = row.original.order ?? 0;
            setOrderToInsert(calculatedOrder);
            setIsCreateQuestionModalOpen(true);
        };

        const handleInsertBelow = () => {
            const calculatedOrder = (row.original.order ?? 0) + 1;
            setOrderToInsert(calculatedOrder);
            setIsCreateQuestionModalOpen(true);
        };

        const questionnaireId = row.original.questionnaireId;

        return (
            <>
                <Dropdown>
                    <Dropdown.Trigger asChild>
                        <Button variant="outlined" size="sm">
                            <Button.Icon name="more" size="sm" />
                        </Button>
                    </Dropdown.Trigger>
                    <Dropdown.Content hasPortal align="end">
                        <Dropdown.Item onClick={handleEdit}>
                            <Dropdown.ItemIcon name="edit" />
                            <Dropdown.ItemText>
                                {t(keys.edit_question)}
                            </Dropdown.ItemText>
                        </Dropdown.Item>
                        {questionnaireId && (
                            <Dropdown.Item onClick={handleInsertAbove}>
                                <Dropdown.ItemIcon name="arrowUp" />
                                <Dropdown.ItemText>
                                    {t(keys.insert_above)}
                                </Dropdown.ItemText>
                            </Dropdown.Item>
                        )}
                        {questionnaireId && (
                            <Dropdown.Item onClick={handleInsertBelow}>
                                <Dropdown.ItemIcon name="arrowDown" />
                                <Dropdown.ItemText>
                                    {t(keys.insert_below)}
                                </Dropdown.ItemText>
                            </Dropdown.Item>
                        )}
                        <Dropdown.Item
                            variant="negative"
                            onClick={handleDelete}
                        >
                            <Dropdown.ItemIcon name="trash" />
                            <Dropdown.ItemText>
                                {t(keys.delete_question)}
                            </Dropdown.ItemText>
                        </Dropdown.Item>
                    </Dropdown.Content>
                </Dropdown>
                <DeleteQuestionModal
                    open={isDeleteQuestionModalOpen}
                    onOpenChange={setIsDeleteQuestionModalOpen}
                    onDelete={handleModalDelete}
                />
                {questionnaireId && (
                    <QuestionModal
                        hasKeepOpenOnConfirmOption={false}
                        loading={false}
                        onConfirm={handleCreateQuestion}
                        open={isCreateQuestionModalOpen}
                        setOpen={setIsCreateQuestionModalOpen}
                        orderToInsert={orderToInsert}
                        questionnaireId={questionnaireId}
                    />
                )}
            </>
        );
    },
};

export const useColumns = (
    selectedLocale: SupportedLocales,
): ColumnDef<QuestionListItem>[] => {
    const columns = useMemo(
        () => [
            checkboxCol,
            questionCol(selectedLocale),
            questionnaireCol,
            groupCol(selectedLocale),
            actionCol,
        ],
        [selectedLocale],
    );
    return columns;
};
